import React, { useState, useEffect } from 'react';
import { Lock, Package, Pill, Truck, ShoppingCart, LayoutDashboard, Users, Settings, DollarSign, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';

const AMOUNT_MODULES = [
  { key: 'show_amounts_inventory', label: 'Inventario', description: 'Ver valor total del inventario', icon: Package, color: 'text-blue-600' },
  { key: 'show_amounts_medications', label: 'Medicamentos', description: 'Ver valor total del inventario de medicamentos', icon: Pill, color: 'text-purple-600' },
  { key: 'show_amounts_dispatches', label: 'Despachos', description: 'Ver totales consolidados de despachos', icon: Truck, color: 'text-green-600' },
  { key: 'show_amounts_compras', label: 'Compras', description: 'Ver totales y costos en Compras', icon: ShoppingCart, color: 'text-orange-600' },
];

const ACCESS_MODULES = [
  { key: 'access_dashboard', label: 'Dashboard', description: 'Acceder a la página principal (Dashboard)', icon: LayoutDashboard, color: 'text-cyan-600' },
  { key: 'access_users', label: 'Usuarios', description: 'Acceder a la administración de usuarios', icon: Users, color: 'text-rose-600' },
  { key: 'access_settings', label: 'Configuración', description: 'Acceder a Configuración del sistema', icon: Settings, color: 'text-slate-600' },
];

export function UserPermissionsDialog({ open, onOpenChange, user, onSaved }) {
  const [permissions, setPermissions] = useState({
    show_amounts_inventory: false,
    show_amounts_medications: false,
    show_amounts_dispatches: false,
    show_amounts_compras: false,
    access_dashboard: false,
    access_users: false,
    access_settings: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setPermissions({
        show_amounts_inventory: !!(user.permissions?.show_amounts_inventory),
        show_amounts_medications: !!(user.permissions?.show_amounts_medications),
        show_amounts_dispatches: !!(user.permissions?.show_amounts_dispatches),
        show_amounts_compras: !!(user.permissions?.show_amounts_compras),
        access_dashboard: !!(user.permissions?.access_dashboard),
        access_users: !!(user.permissions?.access_users),
        access_settings: !!(user.permissions?.access_settings),
      });
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    try {
      setSaving(true);
      await api.put(`/auth/users/${user.id}/permissions`, permissions);
      toast.success(`Permisos actualizados para ${user.name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error('Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="user-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#931A25]" />
            Permisos de Visualización
          </DialogTitle>
          <DialogDescription>
            {user ? (<>Controla qué información monetaria puede ver <strong>{user.name}</strong> en cada módulo.</>) : 'Selecciona un usuario'}
          </DialogDescription>
        </DialogHeader>

        {isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            Este usuario es <strong>Administrador</strong> y siempre ve todos los montos, independientemente de esta configuración.
          </div>
        )}

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          {/* Sección: Acceso a Páginas */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Acceso a Páginas</h3>
            </div>
            <div className="space-y-2">
              {ACCESS_MODULES.map((mod) => {
                const Icon = mod.icon;
                const active = permissions[mod.key];
                return (
                  <div key={mod.key} className={`flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors ${active ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white'}`}>
                    <div className="flex gap-3 flex-1 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-blue-100' : 'bg-slate-100'}`}>
                        <Icon className={`h-5 w-5 ${active ? 'text-blue-700' : mod.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800">{mod.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{mod.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={active}
                      onCheckedChange={(v) => setPermissions({ ...permissions, [mod.key]: v })}
                      disabled={isAdmin}
                      data-testid={`perm-toggle-${mod.key}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sección: Ver Montos */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Ver Montos y Valores</h3>
            </div>
            <div className="space-y-2">
              {AMOUNT_MODULES.map((mod) => {
                const Icon = mod.icon;
                const active = permissions[mod.key];
                return (
                  <div key={mod.key} className={`flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors ${active ? 'border-green-200 bg-green-50/50' : 'border-slate-200 bg-white'}`}>
                    <div className="flex gap-3 flex-1 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-green-100' : 'bg-slate-100'}`}>
                        <Icon className={`h-5 w-5 ${active ? 'text-green-700' : mod.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800">{mod.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{mod.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={active}
                      onCheckedChange={(v) => setPermissions({ ...permissions, [mod.key]: v })}
                      disabled={isAdmin}
                      data-testid={`perm-toggle-${mod.key}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
          Cuando el permiso está <strong>desactivado</strong>, las tarjetas y columnas con montos se ocultan por completo para ese usuario.
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || isAdmin} className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="save-permissions-btn">
            {saving ? 'Guardando...' : 'Guardar Permisos'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
